import { FavoriteBorder } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import Result from 'components/v2/common/Result';
import { Spouse } from '../ClientProfile';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import * as S from '../ClientProfile.styled';

const DESTINATION_COUNTRIES = ['Australia', 'Canada', 'United Kingdom'];

const SpousePartner = ({ spouseData, mobile }: { spouseData: Spouse | null; mobile: boolean }) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    // Same as AssessmentV2/SpousePartnerInfo — region/status only when in destination country.
    const isSpouseInDestinationCountry = DESTINATION_COUNTRIES.includes(
        spouseData?.country_of_residence as string
    );
    const phoneNumber = spouseData?.phone_number ?? null;
    const citizenships = spouseData?.citizenships ?? [];

    function formatDate(date: Date | string | null | undefined) {
        if (date) {
            const stringDate = date.toString();
            const [year, month, day] = stringDate.split('-');
            const months =
                language === 'EN'
                    ? [
                          'January',
                          'February',
                          'March',
                          'April',
                          'May',
                          'June',
                          'July',
                          'August',
                          'September',
                          'October',
                          'November',
                          'December',
                      ]
                    : [
                          'Janvier',
                          'Février',
                          'Mars',
                          'Avril',
                          'Mai',
                          'Juin',
                          'Juillet',
                          'Août',
                          'Septembre',
                          'Octobre',
                          'Novembre',
                          'Décembre',
                      ];

            const monthName = months[parseInt(month, 10) - 1];
            const formattedDate = `${monthName} ${parseInt(day, 10)}, ${year}`;
            return formattedDate;
        } else {
            return '';
        }
    }

    function formatCurrency(amount: number | string | null | undefined) {
        if (amount === null || amount === undefined || amount === '') {
            return '';
        }

        const numericAmount = typeof amount === 'number' ? amount : Number(amount);
        if (!Number.isFinite(numericAmount)) {
            return String(amount);
        }

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(numericAmount);
    }

    return (
        <>
            {spouseData ? (
                <>
                    <S.InfoItem>
                        <Tooltip title={t('gender', { lng: language })}>
                            <S.Label>{t('gender', { lng: language })}</S.Label>
                        </Tooltip>
                        <Tooltip
                            title={t(`${spouseData.gender?.toLowerCase()}`, { lng: language })}
                        >
                            <S.Info mobile={mobile}>
                                {t(`${spouseData.gender?.toLowerCase()}`, { lng: language })}
                            </S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('firstName', { lng: language })}>
                            <S.Label>{t('firstName', { lng: language })}</S.Label>
                        </Tooltip>
                        <Tooltip title={spouseData.firstname}>
                            <S.Info mobile={mobile}>{spouseData.firstname}</S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('lastName', { lng: language })}>
                            <S.Label>{t('lastName', { lng: language })}</S.Label>
                        </Tooltip>
                        <Tooltip title={spouseData.lastname}>
                            <S.Info mobile={mobile}>{spouseData.lastname}</S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    {spouseData.middlename && (
                        <S.InfoItem>
                            <Tooltip title={t('middleName', { lng: language })}>
                                <S.Label>{t('middleName', { lng: language })}</S.Label>
                            </Tooltip>
                            <Tooltip title={spouseData.middlename}>
                                <S.Info mobile={mobile}>{spouseData.middlename}</S.Info>
                            </Tooltip>
                        </S.InfoItem>
                    )}
                    {spouseData.email && (
                        <S.InfoItem>
                            <Tooltip title={t('email', { lng: language })}>
                                <S.Label>{t('email', { lng: language })}</S.Label>
                            </Tooltip>
                            <Tooltip title={spouseData.email}>
                                <S.Info mobile={mobile}>{spouseData.email}</S.Info>
                            </Tooltip>
                        </S.InfoItem>
                    )}
                    {phoneNumber && (
                        <S.InfoItem>
                            <Tooltip title={t('phoneNumber', { lng: language })}>
                                <S.Label>{t('phoneNumber', { lng: language })}</S.Label>
                            </Tooltip>
                            <Tooltip title={phoneNumber}>
                                <S.Info mobile={mobile}>{phoneNumber}</S.Info>
                            </Tooltip>
                        </S.InfoItem>
                    )}
                    <S.InfoItem>
                        <Tooltip title={t('dateOfBirth', { lng: language })}>
                            <S.Label mobile={mobile}>{t('dateOfBirth', { lng: language })}</S.Label>
                        </Tooltip>
                        <Tooltip title={formatDate(spouseData.birth_date)}>
                            <S.Info mobile={mobile}>{formatDate(spouseData.birth_date)}</S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('nationality', { lng: language })}>
                            <S.Label>{t('nationality', { lng: language })}</S.Label>
                        </Tooltip>
                        <Tooltip title={spouseData.nationality}>
                            <S.Info mobile={mobile}>{spouseData.nationality}</S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    {citizenships.length > 0 && (
                        <S.InfoItem>
                            <Tooltip title={t('citizenship', { lng: language })}>
                                <S.Label>{t('citizenship', { lng: language })}</S.Label>
                            </Tooltip>
                            <Tooltip title={citizenships.join(', ')}>
                                <S.Info mobile={mobile}>{citizenships.join(', ')}</S.Info>
                            </Tooltip>
                        </S.InfoItem>
                    )}
                    <S.InfoItem>
                        <Tooltip title={t('countryOfResidence', { lng: language })}>
                            <S.Label mobile={mobile}>
                                {t('countryOfResidence', { lng: language })}
                            </S.Label>
                        </Tooltip>
                        <Tooltip title={spouseData.country_of_residence}>
                            <S.Info mobile={mobile}>{spouseData.country_of_residence}</S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    {isSpouseInDestinationCountry && (
                        <>
                            <S.InfoItem>
                                <Tooltip title={t('regionOfResidence', { lng: language })}>
                                    <S.Label mobile={mobile}>
                                        {t('regionOfResidence', { lng: language })}
                                    </S.Label>
                                </Tooltip>
                                <Tooltip title={spouseData.region_of_residence}>
                                    <S.Info mobile={mobile}>
                                        {spouseData.region_of_residence}
                                    </S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('statusInCountryOfResidence', { lng: language })}>
                                    <S.Label mobile={mobile}>
                                        {t('statusInCountryOfResidence', { lng: language })}
                                    </S.Label>
                                </Tooltip>
                                <Tooltip title={spouseData.residency_status}>
                                    <S.Info mobile={mobile}>{spouseData.residency_status}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                        </>
                    )}
                    <S.InfoItem>
                        <Tooltip title={t('residenceStatusSince', { lng: language })}>
                            <S.Label mobile={mobile}>
                                {t('residenceStatusSince', { lng: language })}
                            </S.Label>
                        </Tooltip>
                        <Tooltip title={formatDate(spouseData.residence_since)}>
                            <S.Info mobile={mobile}>
                                {formatDate(spouseData.residence_since)}
                            </S.Info>
                        </Tooltip>
                    </S.InfoItem>
                    <S.InfoItem>
                        <Tooltip title={t('income', { lng: language })}>
                            <S.Label mobile={mobile}>{t('income', { lng: language })}</S.Label>
                        </Tooltip>
                        {spouseData.income ? (
                            <Tooltip title={formatCurrency(spouseData.income)}>
                                <S.Info mobile={mobile}>{formatCurrency(spouseData.income)}</S.Info>
                            </Tooltip>
                        ) : null}
                    </S.InfoItem>
                </>
            ) : (
                <Result
                    icon={<FavoriteBorder sx={{ fontSize: 32 }} />}
                    title={t('noInfo', { lng: language })}
                    description="No spouse or partner details have been added yet."
                />
            )}
        </>
    );
};

export default SpousePartner;
